import React from "react";
import { cn } from "../../lib/utils";
import { MoreVertical, Map as MapIcon, Globe, Lock, Users, Calendar, Hash, Layers, Maximize } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../internal/primitives/popover";
import type { MapCustomMapLibraryCardProps } from "../../contracts/map-custom-maps";

export function MapCustomMapLibraryCard({
  item,
  onAction,
  className,
}: MapCustomMapLibraryCardProps) {
  const {
    id,
    title,
    description,
    eyebrow,
    scopeLabel,
    ownerLabel,
    updatedLabel,
    mapIdLabel,
    sourceSummary,
    viewportSummary,
    featured,
    thumbnailLabel,
    statusBadges = [],
    stats = [],
    primaryAction,
    secondaryActions = [],
  } = item;

  const getScopeIcon = (label?: string | null) => {
    if (!label) return <Globe className="w-3.5 h-3.5" />;
    const lower = label.toLowerCase();
    if (lower.includes("private") || lower.includes("personal") || lower.includes("tenant")) return <Lock className="w-3.5 h-3.5" />;
    if (lower.includes("shared") || lower.includes("team")) return <Users className="w-3.5 h-3.5" />;
    return <Globe className="w-3.5 h-3.5" />;
  };

  const getToneClasses = (tone?: string) => {
    switch (tone) {
      case "success":
        return "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20";
      case "warning":
        return "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20";
      case "danger":
        return "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20";
      case "muted":
        return "bg-muted text-muted-foreground border-transparent";
      default:
        return "bg-primary/10 text-primary border-primary/20";
    }
  };

  return (
    <div
      className={cn(
        "group relative flex flex-col bg-card text-card-foreground border rounded-xl overflow-hidden transition-all duration-200 hover:shadow-lg hover:border-primary/30",
        featured && "ring-2 ring-primary ring-offset-2 ring-offset-background",
        className
      )}
    >
      {/* Thumbnail / Placeholder */}
      <div className="relative aspect-[16/9] bg-muted flex items-center justify-center overflow-hidden border-b">
        {thumbnailLabel ? (
          <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-muted-foreground/50 uppercase tracking-wider text-center px-4">
            {thumbnailLabel}
          </div>
        ) : (
          <MapIcon className="w-12 h-12 text-muted-foreground/20" />
        )}
        
        {/* Badges Overlay */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {scopeLabel && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-background/90 backdrop-blur-sm border text-[10px] font-bold uppercase tracking-tight text-foreground shadow-sm">
              {getScopeIcon(scopeLabel)}
              {scopeLabel}
            </div>
          )}
          {statusBadges.map((badge) => (
            <div
              key={badge.id}
              className={cn(
                "px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-tight shadow-sm",
                getToneClasses(badge.tone)
              )}
            >
              {badge.label}
            </div>
          ))}
        </div>

        {featured && (
          <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-tight shadow-sm">
            Featured
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 p-5 flex flex-col">
        <div className="mb-4">
          {eyebrow && (
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
              {eyebrow}
            </div>
          )}
          <h3 className="text-lg font-semibold leading-tight mb-1 line-clamp-1 group-hover:text-primary transition-colors">
            {title}
          </h3>
          {description && (
            <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">
              {description}
            </p>
          )}
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 gap-y-3 gap-x-4 mb-5 text-[11px]">
          {ownerLabel && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="w-3.5 h-3.5 opacity-60" />
              <span className="truncate">{ownerLabel}</span>
            </div>
          )}
          {updatedLabel && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-3.5 h-3.5 opacity-60" />
              <span className="truncate">{updatedLabel}</span>
            </div>
          )}
          {sourceSummary && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Layers className="w-3.5 h-3.5 opacity-60" />
              <span className="truncate">{sourceSummary}</span>
            </div>
          )}
          {viewportSummary && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Maximize className="w-3.5 h-3.5 opacity-60" />
              <span className="truncate">{viewportSummary}</span>
            </div>
          )}
          {mapIdLabel && (
            <div className="flex items-center gap-2 text-muted-foreground col-span-2">
              <Hash className="w-3.5 h-3.5 opacity-60" />
              <span className="truncate font-mono opacity-80">{mapIdLabel}</span>
            </div>
          )}
        </div>

        {/* Stats Row */}
        {stats.length > 0 && (
          <div className="flex items-center gap-4 py-3 border-t border-b border-muted/50 mb-5">
            {stats.map((stat) => (
              <div key={stat.id} className="flex flex-col">
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  {stat.label}
                </span>
                <span className="text-sm font-semibold tabular-nums">
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="mt-auto flex items-center gap-2">
          {primaryAction && (
            <button
              onClick={() => onAction(id, primaryAction.id)}
              disabled={primaryAction.disabled}
              className={cn(
                "flex-1 inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-all shadow-sm",
                primaryAction.tone === "danger"
                  ? "bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  : "bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              )}
            >
              {primaryAction.label}
            </button>
          )}
          
          {secondaryActions.length === 1 ? (
            <button
              onClick={() => onAction(id, secondaryActions[0].id)}
              disabled={secondaryActions[0].disabled}
              title={secondaryActions[0].description || undefined}
              className={cn(
                "inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors border shadow-sm",
                secondaryActions[0].tone === "danger"
                  ? "text-red-600 hover:bg-red-50 border-red-200 dark:border-red-900/50"
                  : "hover:bg-muted text-muted-foreground hover:text-foreground"
              )}
            >
              {secondaryActions[0].label}
            </button>
          ) : secondaryActions.length > 1 ? (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors border shadow-sm"
                  aria-label="More actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-48 p-1">
                <div className="flex flex-col">
                  {secondaryActions.map((action) => (
                    <button
                      key={action.id}
                      onClick={() => onAction(id, action.id)}
                      disabled={action.disabled}
                      className={cn(
                        "flex flex-col w-full px-3 py-2 rounded-md text-left transition-colors hover:bg-muted disabled:opacity-50 disabled:pointer-events-none",
                        action.tone === "danger" && "text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10"
                      )}
                    >
                      <span className="text-sm font-medium">{action.label}</span>
                      {action.description && (
                        <span className="text-[10px] text-muted-foreground">{action.description}</span>
                      )}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          ) : null}
        </div>
      </div>
    </div>
  );
}
